import properties from '../../../constants/Properties';
import storeManager from '../../../store/StoreManager';
import { addAudioToTimeline, setMarkerPosition, setTimelineWidth, refreshAudioTrack } from '../../../actions/AudioEditorActions';
import uuid from 'uuid';
import { addNewActionToHistory } from '../../../actions/UndoRedoActions';
import saveManager from '../../utils/SaveManager';
import durationUtils from '../../utils/DurationUtils';
import playController from '../../utils/PlayController';
import audioToolUtils from '../../audioTool/utils/AudioToolUtils';
import platformDetection from '../../utils/PlatformDetection';

const audioEditorUtils = {
    isControlPointAction: false,
    isVolumeAction: false,
    slidesPosition: undefined,
    animationId: undefined,
    markerUpdater: undefined,
    refreshAllSlidesRenderers: false,

    currentTrackMenu: -1,
    maxZoom: 13,

    getSlideWidth(time, zoom = -1) {
        if (zoom != -1)
            return time * properties.SLIDE_ITEM_WIDTH * zoom;
        else
            return time * properties.SLIDE_ITEM_WIDTH * this.getZoomValue();
    },
    getZoomValue() {
        const state = storeManager.getState();
        var zoom = state.audioEditor.zoomValue;
        var value;
        var listOfValues = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 1.0, 1.2, 1.5, 1.7, 2.0, 2.2];

        if (zoom == undefined) {
            if (audioToolUtils.isAudioTool())
                value = 0.2;
            else
                value = 0.5;
        } else
            value = listOfValues[zoom];

        return value;
    },
    getTimelinesLength() {
        const state = storeManager.getState();
        const timelines = state.audioEditor.timelines;

        if (timelines)
            return timelines.length;
        else
            return 3;
    },
    getAudioEditorHeight() {
        const offset = 35; // ruler height
        return this.getTimelinesLength() * audioToolUtils.timelineHeight() + offset;
    },
    getTimelineMaxTime() {
        var maxWidth = this.getTimelineWidth();

        return maxWidth / (properties.SLIDE_ITEM_WIDTH * this.getZoomValue());
    },
    getTimelineWidth() {
        var widthFromSlides = this.getTimelineWidthFromSlidesDuration();
        var widthFromAudios = this.getTimelineWidthFromAudios();

        if (widthFromAudios > widthFromSlides)
            return widthFromAudios;
        else
            return widthFromSlides;
    },
    getTimelineWidthFromSlidesDuration() {
        const state = storeManager.getState();
        var accordionWidth = state.accordion.width;

        if (accordionWidth == undefined)
            accordionWidth = properties.BROWSER_WIDTH;

        var windowWidth = state.production.windowWidth;

        if (!(windowWidth > 0))
            windowWidth = window.innerWidth;

        var slidesTotalDuration = this.getSlidesMaxPosition();
        var width = this.getSlideWidth(slidesTotalDuration);
        var minimumWidth = windowWidth - accordionWidth - audioToolUtils.buttonsHolderWidth();

        if (width < minimumWidth)
            width = minimumWidth;

        return width;
    },
    getTimelineWidthFromAudios() {
        return this.getSlideWidth(this.getTimelinesMaxPosition());
    },
    getSlidesMaxPosition() {
        const state = storeManager.getState();
        const currentIndex = state.currentSlide.currentSlideIndex;
        const slides = state.production.slides;
        var i;
        var totalTime = 0;

        for (i=0; i<slides.length; i++) {
            var time;

            if (i == currentIndex)
                time = state.currentSlide.model.time;
            else
                time = slides[i].time;

            totalTime = totalTime + time;
        }

        return totalTime;
    },
    maxPosition: -1,
    getTimelinesMaxPosition(currentTimelines) {
        var timelines;

        if (currentTimelines)
            timelines = currentTimelines;
        else {
            var audioEditor = storeManager.getState().audioEditor;

            if (audioEditor == undefined)
                return 0;

            timelines = audioEditor.timelines;
        }

        var i;
        var j;
        var maxPos = 0;

        for (i=0; i<timelines.length; i++) {
            var audios = timelines[i].audios;

            for (j=0; j<audios.length; j++) {
                var pos = audios[j].left + audios[j].duration;

                if (pos > maxPos)
                    maxPos = pos;
            }
        }

        this.maxPosition = maxPos;

        return maxPos;
    },
    getExportTotalTime() {
        if (audioToolUtils.isAudioTool()) {
            var time = this.getTimelinesMaxPosition();

            return Math.floor(time * 1000);
        }

        var slidesTime = this.getSlidesMaxPosition();
        var totalTime;

        if (playController.isAudioEditorAvailable()) {
            var timelineTime = this.getTimelinesMaxPosition();
            totalTime = Math.max(slidesTime, timelineTime);
        } else {
            totalTime = slidesTime;
        }

        return Math.floor(totalTime * 1000);
    },
    getTotalDuration() {
        const state = storeManager.getState();
        const currentIndex = state.currentSlide.currentSlideIndex;
        const slides = state.production.slides;
        var i;
        var totalDuration = 0;

        for (i=0; i<slides.length; i++) {
            var time;

            if (i == currentIndex)
                time = state.currentSlide.model.time;
            else
                time = slides[i].time;

            totalDuration = totalDuration + time;
        }

        return totalDuration;
    },
    getItemPosition(left) {
        var totalTime = this.getTotalDuration();
        var totalWidth = this.getSlideWidth(totalTime);

        return (totalWidth * left) / totalTime;
    },
    getItemTimePosition(leftPos) {
        var totalTime = this.getTotalDuration();
        var totalWidth = this.getSlideWidth(totalTime);

        return (totalTime * leftPos) / totalWidth;
    },
    getDelimitators() {
        const state = storeManager.getState();
        const slides = state.production.slides;
        const currentIndex = state.currentSlide.currentSlideIndex;

        if (slides.length > 0) {
            var i;
            var width = 0;
            var delimitators = [];

            for (i = 0; i < slides.length; i++) {
                var time;

                if (i == currentIndex)
                    time = state.currentSlide.model.time;
                else
                    time = slides[i].time;

                width = width + this.getSlideWidth(time);
                delimitators.push(width);
            }

            return delimitators;
        } else
            return [];
    },
    getCurrentSlideWidth() {
        const state = storeManager.getState();
        const time = state.currentSlide.model.time;

        return this.getSlideWidth(time);
    },
    getMarkerPositionForSlideIndex(index = -1) {
        const state = storeManager.getState();
        const slides = state.production.slides;
        const currentIndex = state.currentSlide.currentSlideIndex;

        if (slides.length > 0) {
            var i;
            var currentTime = 0;
            var delimitators = [];
            var time;

            for (i = 0; i < slides.length; i++) {
                delimitators.push(currentTime);

                if (i == currentIndex)
                    time = state.currentSlide.model.time;
                else
                    time = slides[i].time;

                currentTime = currentTime + time;
            }

            if (index == -1)
                index = currentIndex;

            return delimitators[index];
        } else
            return -1;
    },
    checkMarkerPositionForCurrentSlide() {
        if (playController.isAudioEditorMode() && !playController.isPlaying) {
            setTimeout(function() {
                const dispatch = storeManager.getStore().dispatch;
                var currentMarkerPosition = this.getMarkerPositionForSlideIndex();
                dispatch(setMarkerPosition(currentMarkerPosition));
            }.bind(this), 10);
        }
    },
    getSlideItem(audio) {
        var item = {};

        item.title = audio.title;
        item.href = audio.href;
        item.uuid = uuid.v4();

        if (playController.isAudioEditorMode()) {
            item.left = audio.left;
            item.outPoint = 0;

            if (audio.durationInSeconds > 0)
                item.duration = audio.durationInSeconds; //keep real seconds
            else
                item.duration = durationUtils.transformDurationFromStringToSeconds(audio.duration);
        } else {
            if (audio.isStorySound) {
                item.left = 0;
                item.duration = this.getTotalDuration();
            } else {
                var delimitators = this.getDelimitators();
                const state = storeManager.getState();
                const currentIndex = state.currentSlide.currentSlideIndex;
                var position = 0;

                if (currentIndex > 0)
                    position = this.getItemTimePosition(delimitators[currentIndex - 1]);

                item.left = position;
                item.duration = state.currentSlide.model.time;
            }
        }

        return item;
    },
    getStartColor(index) {
        var colors = ['#ff0038', '#0095de', '#f8c700', '#00d773', '#ff00ff', '#aa95ff', '#f8c7ff', '#d0d77f', '#ffa999', '#0f22df', '#f8a7a0', '#25d223', '#ff0038', '#0095de', '#f8c700', '#00d773', '#ff00ff', '#aa95ff', '#f8c7ff', '#d0d77f', '#ffa999', '#0f22df', '#f8a7a0', '#25d223'];

        return colors[index];
    },
    getEndColor(index) {
        var colors = ['#ff0038', '#0095de', '#f8c700', '#00d773', '#ff00ff', '#aa95ff', '#f8c7ff', '#d0d77f', '#ffa999', '#0f22df', '#f8a7a0', '#25d223', '#ff0038', '#0095de', '#f8c700', '#00d773', '#ff00ff', '#aa95ff', '#f8c7ff', '#d0d77f', '#ffa999', '#0f22df', '#f8a7a0', '#25d223'];

        return colors[index];
    },
    getGradient(index) {
        if (this.isVolumeMute(index))
            return '#999A9C';

        return this.getStartColor(index);
        return 'linear-gradient(to right, ' + this.getStartColor(index) + ' , ' + this.getEndColor(index) + ')';
    },
    getSoloIndex() {
        const state = storeManager.getState();
        var timelines = state.audioEditor.timelines;
        var i;
        var soloIndex = -1;

        for (i=0; i<timelines.length; i++) {
            if (timelines[i].isSolo)
                soloIndex = i;
        }

        return soloIndex;
    },
    isVolumeMute(index) {
        var soloIndex = this.getSoloIndex();
        const state = storeManager.getState();
        var timelines = state.audioEditor.timelines;

        if (soloIndex >= 0) { // is Solo activated
            if (index == soloIndex)
                return false;
            else
                return true;
        } else
            return timelines[index].isMute;
    },
    addItemToTimeline(item, type = 0) {
        const dispatch = storeManager.getStore().dispatch;
        saveManager.isContentChanged(true);

        var dropPosition = this.getItemDropPosition(item);
        item.left = dropPosition.left;

        if (playController.isAudioEditorMode()) {
            dispatch(addAudioToTimeline(dropPosition.index, item));
        } else {
            switch (type) {
                case 0: { // Music: story sound
                    item.isStorySound = true;
                    dispatch(addAudioToTimeline(2, item));
                    break;
                }

                case 1: { // Sound effect
                    dispatch(addAudioToTimeline(1, item));
                    break;
                }

                case 2: { // My sound
                    dispatch(addAudioToTimeline(1, item));
                    break;
                }
            }
        }

        saveManager.isContentChanged(true);
        this.checkTimelineWidth();
        this.addActionToHistory();
    },
    lastIndex: -1,
    getItemDropPosition(item, isRecording = false) {
        const state = storeManager.getState();
        var markerPosition = isRecording ? item.left : state.audioEditor.markerPosition;
        var timelines = state.audioEditor.timelines;
        var position = {};
        var i, j, audios;
        var freeTimeline;
        var currentItemDuration;
        var noFreeTimeline = true;

        if (item.durationInSeconds > 0)
            currentItemDuration = item.durationInSeconds;
        else {
            if (item.duration > 0)
                currentItemDuration = item.duration;
            else
                currentItemDuration = durationUtils.transformDurationFromStringToSeconds(item.duration);
        }

        position.left = markerPosition;
        position.index = 0;

        for (i=0; i<timelines.length; i++) {
            audios = timelines[i].audios;
            freeTimeline = true;

            for (j=0; j<audios.length; j++) {
                if (markerPosition >= audios[j].left && markerPosition <= (audios[j].left + audios[j].duration)) { // the marker it's already on top of an audio
                    freeTimeline = false;
                    break;
                } else if ((markerPosition + currentItemDuration) > audios[j].left && (audios[j].left + audios[j].duration) > markerPosition) { // if the current item will be added at marker position then it will overlap the new audio
                    freeTimeline = false;
                    break;
                }
            }

            if (freeTimeline) {
                position.index = i;
                noFreeTimeline = false;
                break;
            }
        }

        if (this.lastIndex != -1 && noFreeTimeline)
            position.index = this.lastIndex + 1;

        if (position.index >= timelines.length)
            position.index = 0;

        this.lastIndex = position.index;

        return position;
    },
    getCurrentSlidePosition(slideUid, includeCurrentSlide = false) {
        const state = storeManager.getState();
        var slides = state.production.slides;
        var i;
        var currentTime = 0;

        if (slideUid == '')
            slideUid = state.currentSlide.model.uuid;

        for (i = 0; i < slides.length; i++) {
            if (slides[i].uuid == slideUid) {
                if (includeCurrentSlide)
                    currentTime = currentTime + slides[i].time;

                break;
            } else
                currentTime = currentTime + slides[i].time;
        }

        return currentTime;
    },
    getSoundItemUids(title) {
        if (title && title.indexOf('.mp3') > 0)
            title = title.replace('.mp3', '');

        var audioEditor = storeManager.getState().audioEditor;
        var timelines = audioEditor.timelines;
        var audios;
        var i, j;
        var uidArray = [];

        for (i=0; i<timelines.length; i++) {
            audios = timelines[i].audios;

            for (j = 0; j < audios.length; j++) {
                if (audios[j] && audios[j].title && audios[j].title.indexOf(title) != -1)
                    uidArray.push(audios[j].uuid);
            }
        }

        return uidArray;
    },
    message: '',
    addRecordedSoundToTimeline(href, title, position, duration = -1) {
        const dispatch = storeManager.getStore().dispatch;
        saveManager.isContentChanged(true);

        var sound = {};

        sound.href = href;
        sound.title = title;
        sound.left = position;
        sound.showControlPoint = true;
        sound.uuid = uuid.v4();

        if (duration > 0)
            sound.duration = duration;
        else
            sound.duration = 1; // add default 1 second until the sound will be loaded to get the correct time

        if (playController.isAudioEditorMode()) {
            var dropPosition = this.getItemDropPosition(sound, true);
            dispatch(addAudioToTimeline(dropPosition.index, sound));
        } else
            dispatch(addAudioToTimeline(0, sound));

        saveManager.isContentChanged(true);
        this.checkTimelineWidth();
        this.addActionToHistory();

        if (position != -1)
            dispatch(setMarkerPosition(position));
    },
    transformToSeconds(counter) {
        var minutes = Math.floor(counter / 60);
        var seconds = counter - minutes * 60;
        var text = '';

        if (minutes < 10)
            text = text + '0' + minutes;
        else
            text = text + minutes;

        text = text + ':';

        if (seconds < 10)
            text = text + '0' + seconds;
        else
            text = text + seconds;

        return text;
    },
    getMarkerPosition() {
        const state = storeManager.getState();
        const markerPosition = state.audioEditor.markerPosition;

        if (markerPosition == undefined)
            return -1;
        else
            return markerPosition;
    },
    addActionToHistory() {
        setTimeout(function() {
            const dispatch = storeManager.getStore().dispatch;
            dispatch(addNewActionToHistory());
        }.bind(this), 10);
    },
    getTimelineOffset() {
        var offsetTime;

        if (platformDetection.isIOS()) {
            if (this.maxPosition > 4 * 60)
                offsetTime = 10;
            else
                offsetTime = 60 * 2;
        } else {
            if (this.maxPosition > 10 * 60)
                offsetTime = 30;
            else
            if (this.maxPosition < 5 * 60)
                offsetTime = 3 * 60;
            else
                offsetTime = 1 * 60;
        }

        return this.getSlideWidth(offsetTime);
    },
    checkTimelineWidth(updateScroll = false, oldZoom = -1, forceWidth = false) {
        if (!playController.isAudioEditorMode())
            return;

        setTimeout(function() {
            const state = storeManager.getState();
            const dispatch = storeManager.getStore().dispatch;
            const oldWidth = state.audioEditor.timelineWidth;
            var currentWidth = this.getTimelineWidth();
            var timelineWidthChanged = false;
            var offset = this.getTimelineOffset(currentWidth);

            if (oldWidth < currentWidth || forceWidth) {
                dispatch(setTimelineWidth(currentWidth + offset));
                timelineWidthChanged = true;
                updateScroll=true;
            }

            if (oldZoom != -1)
                this.checkScrollForZoom();
            else {
                if (updateScroll)
                    this.checkScroll(timelineWidthChanged, currentWidth);
            }

            // only when the left menu si collapse/expanded
            if (!this.isControlPointAction && !this.isVolumeAction && forceWidth)
                dispatch(refreshAudioTrack(Math.random()));
        }.bind(this), 5);
    },
    zoomTime: -1,
    checkScrollForZoom() {
        if (this.zoomTime < 0 || ((new Date().getTime() - this.zoomTime) > 2000))
            playController.getOldScrollPosition();

        this.zoomTime = new Date().getTime();
    },
    checkScroll(timelineWidthChanged) {
        setTimeout(function() {
            var comp = document.getElementById('slidesList');
            var scrollPosition = this.getSlideWidth(this.getMarkerPosition());

            if (!timelineWidthChanged) {
                const state = storeManager.getState();
                const windowWidth = state.production.windowWidth;
                const accordionWidth = state.accordion.width;
                var visibleArea = windowWidth - accordionWidth - audioToolUtils.buttonsHolderWidth() - this.getCurrentSlideWidth();

                scrollPosition = scrollPosition - visibleArea;
            }

            if (comp)
                comp.scrollLeft = scrollPosition;
        }.bind(this), 100);
    },
    getSelectedAudio() {
        var audioEditor = storeManager.getState().audioEditor;

        if (audioEditor == undefined)
            return undefined;

        var timelines = audioEditor.timelines;
        var i;
        var j;

        for (i=0; i<timelines.length; i++) {
            var audios = timelines[i].audios;

            for (j=0; j<audios.length; j++) {
                if (audios[j] && audios[j].showControlPoint) {
                    return {model: audios[j], index: i};
                }
            }
        }

        return undefined;
    },
    updateMarker() {
        setTimeout(function() {
            if (this.markerUpdater)
                this.markerUpdater();
        }.bind(this), 10);
    },
    updateVolumePoints(points, oldInPoint, newInPoint, oldOutPoint, newOutPoint, newDuration) {
        if (!points || points.length < 2)
            return undefined;

        if (oldInPoint == newInPoint && oldOutPoint == newOutPoint)
            return points;

        var offset;
        var newPoints = [];
        var i;
        var isFirst = false;
        var newVol;

        if (!newInPoint)
            newInPoint = 0;

        if (!newOutPoint)
            newOutPoint = 0;

        if (!oldInPoint)
            oldInPoint = 0;

        if (!oldOutPoint)
            oldOutPoint = 0;

        if (oldInPoint != newInPoint) { // left Arrow handling
            offset = Math.abs(oldInPoint - newInPoint);

            if (oldInPoint > newInPoint) { // drag to the left
                newPoints.push({time: 0, vol: points[0].vol});

                for (i=0; i<points.length; i++) {
                    newPoints.push({time: points[i].time + offset, vol: points[i].vol});
                }
            } else { // drag to the right
                for (i=0; i<points.length; i++) {
                    if (points[i].time > offset) {
                        if (!isFirst) {
                            if (i > 0)
                                newVol = this.getIntersectionPoint(points[i].time, points[i].vol, points[i-1].time, points[i-1].vol, offset);
                            else
                                newVol = points[i].vol;

                            newPoints.push({time: 0, vol: newVol});
                        }

                        newPoints.push({time: points[i].time - offset, vol: points[i].vol});
                        isFirst = true;
                    }
                }
            }
        } else { // right Arrow handling
            offset = Math.abs(oldOutPoint - newOutPoint);

            if (oldOutPoint < newOutPoint) { // drag to the left
                for (i=0; i<points.length; i++) {
                    if (points[i].time < newDuration) {
                        newPoints.push({time: points[i].time, vol: points[i].vol});
                    } else {
                        if (i > 1)
                            newVol = this.getIntersectionPoint(points[i].time, points[i].vol, points[i-1].time, points[i-1].vol, newDuration);
                        else
                            newVol = points[i].vol;

                        newPoints.push({time: newDuration, vol: newVol});
                        break;
                    }
                }
            } else { // drag to the right
                for (i=0; i<points.length; i++) {
                    newPoints.push({time: points[i].time, vol: points[i].vol});
                }

                newPoints.push({time: newDuration, vol: points[points.length - 1].vol});
            }
        }

        return newPoints;
    },
    getControlPointsForSplitItems(points, leftDuration) {
        var leftPoints = [];
        var rightPoints = [];
        var i;
        var isFirst = false;

        for (i=0; i<points.length; i++) {
            if (points[i].time > leftDuration) {
                if (!isFirst) {
                    if (points[i-1] && points[i]) {
                        var volume = this.getIntersectionPoint(points[i - 1].time, points[i - 1].vol, points[i].time, points[i].vol, leftDuration);

                        leftPoints.push({time: leftDuration, vol: volume});
                        rightPoints.push({time: 0, vol: volume});
                    }
                }

                rightPoints.push({time: (points[i].time - leftDuration), vol: points[i].vol});

                isFirst = true;
            } else {
                leftPoints.push(points[i]);
            }
        }

        return {left: leftPoints, right: rightPoints};
    },
    getIntersectionPoint(x1, y1, x2, y2, x) {
        return ((x - x1) * (y2 -y1) + y1 * (x2 - x1)) / (x2 - x1);
    },
    extractContentItemId(link) {
        if (!link)
            return -1;

        var index = link.indexOf('file/ci/');
        var index2 = link.indexOf('/aaa');
        var id = -1;

        if (index > 0 && index2 > 0)
            id = link.substring(index + 8, index2);

        return parseInt(id);
    },
    ensureHasContentItem(audio) {
        var contentItemId = this.extractContentItemId(audio.href);

        if (contentItemId > 0)
            audio.contentItemId = contentItemId;
    },
    isAudioSelected() {
        var audioEditor = storeManager.getState().audioEditor;

        if (audioEditor == undefined)
            return false;

        var timelines = audioEditor.timelines;
        var i;

        for (i=0; i<timelines.length; i++) {
            if (timelines[i].isSelected)
                return true;
        }

        return false;
    },
    checkAudioControlPoints(data) {
        var timelines = data.timelines;
        var i, j, k;

        for (i=0; i<timelines.length; i++) {
            var audios = timelines[i].audios;

            for (j=0; j<audios.length; j++) {
                var audio = audios[j];

                if (audio.points) {
                    var newPoints = [];
                    var duration = audio.duration;

                    for (k=0; k<audio.points.length; k++) {
                        var point = audio.points[k];

                        if (point.time < 0 || point.time > duration) {
                            // wrong
                        } else {
                            newPoints.push(point);
                        }
                    }

                    audio.points = newPoints;
                }
            }
        }

        return data;
    },
    getMaxTimelines() {
        var maxTimelines;

        if (audioToolUtils.isAudioTool()) {
            if (platformDetection.isIOS()) {
                if (platformDetection.isIOSApp())
                    maxTimelines = 7;
                else
                    maxTimelines = 5;
            } else
                maxTimelines = 7;
        } else
            maxTimelines = 5;

        var maxTimelinesRelatedToHeight = this.getMaxTimelinesRelatedToHeight();

        if (maxTimelinesRelatedToHeight < maxTimelines)
            return maxTimelinesRelatedToHeight;

        return maxTimelines;
    },
    getMaxTimelinesRelatedToHeight() {
        // if (audioToolUtils.isAudioTool()) {
        //     var windowHeight = storeManager.getState().production.windowHeight;
        //     var freeHeight = windowHeight - 200;
        //     var trackHeight = audioToolUtils.timelineHeight();
        //
        //     return Math.floor(freeHeight / trackHeight);
        // } else
            return 20;
    },
    addControlPointHandler: undefined
};

export default audioEditorUtils;



/** WEBPACK FOOTER **
 ** ./src/components/audioEditor/utils/AudioEditorUtils.js
 **/